import { Ionicons } from "@expo/vector-icons";
import React from "react";
import {
  View,
  Text,
  Platform,
  type DimensionValue,
} from "react-native";

import LottieView from "../../../components/CrossPlatformLottie";

const IQES = {
  panel: "#0B1026",
  panelDeep: "#070A18",
  panelLift: "#151B37",
  gold: "#F8C84E",
  goldSoft: "#FFE78A",
  purpleDeep: "#21103D",
  ivory: "#FFF7DF",
  text: "#F9F0D6",
  muted: "#C9BEDA",
  border: "rgba(248, 200, 78, 0.42)",
  borderStrong: "rgba(248, 200, 78, 0.72)",
};

interface ResultsHeaderBlockProps {
  trophySource: unknown;
  trophyMaxHeight: number;
  headerText: string;
  subHeaderText: string;
  verseLineDynamic?: string;
  eyebrowText?: string;
  contextTitle?: string;
}

const ResultsHeaderBlock: React.FC<ResultsHeaderBlockProps> = ({
  trophySource,
  trophyMaxHeight,
  headerText,
  subHeaderText,
  verseLineDynamic,
  eyebrowText,
  contextTitle,
}) => {
  // Fallback verse to ensure verse always shows
  const fallbackVerse = "I have fought the good fight, I have finished the race, I have kept the faith. — 2 Timothy 4:7";

  // Clean and validate the verse text
  const cleanVerse = verseLineDynamic?.toString().trim();
  const displayVerse = cleanVerse && cleanVerse.length > 0 ? cleanVerse : fallbackVerse;
  const verseCardMaxWidth = Platform.select<DimensionValue>({
    web: 380,
    default: '90%',
  });
  const verseCardMinWidth = Platform.select<number | undefined>({
    web: undefined,
    default: 280,
  });


  return (
    <View style={{
      alignItems: 'center',
      marginBottom: 18,
      paddingHorizontal: 20,
      width: "100%",
    }}>
      <View
        style={{
          width: 72,
          height: 72,
          borderRadius: 36,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(248, 200, 78, 0.14)",
          borderWidth: 1,
          borderColor: IQES.borderStrong,
          marginBottom: 14,
        }}
      >
        <Ionicons name="trophy" size={34} color={IQES.goldSoft} />
      </View>
      {/* Trophy Animation with Premium Container - Temporarily disabled to identify source */}
      {false && trophySource && trophyMaxHeight && trophyMaxHeight > 0 && (
        <View style={{
          width: '100%',
          maxHeight: trophyMaxHeight,
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 20,
          backgroundColor: IQES.panel,
          borderRadius: 24,
          padding: 16,
          borderWidth: 1,
          borderColor: IQES.border,
          ...Platform.select({
            ios: {
              shadowColor: IQES.gold,
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.16,
              shadowRadius: 12,
            },
            android: {
              elevation: 4,
            },
            web: {
              boxShadow: '0 4px 16px rgba(248, 200, 78, 0.16)',
            },
          }),
        }}>
          <LottieView
            source={trophySource as never}
            autoPlay
            loop={false}
            resizeMode="contain"
            style={{ width: '100%', height: '100%', maxHeight: trophyMaxHeight }}
          />
        </View>
      )}

      {eyebrowText ? (
        <Text
          style={{
          fontSize: 12,
          fontWeight: '800',
          textAlign: 'center',
          marginBottom: 6,
            color: IQES.goldSoft,
            lineHeight: 16,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          {eyebrowText}
        </Text>
      ) : null}
      <Text
        style={{
          fontSize: 36,
          fontWeight: '800',
          textAlign: 'center',
          marginBottom: 4,
          color: IQES.ivory,
          lineHeight: 42,
          letterSpacing: -0.4,
        }}
        accessibilityRole="header"
        accessibilityLabel={headerText}
      >
        {headerText}
      </Text>

      {/* Premium Subheader */}
      <Text
        style={{
          fontSize: 18,
          fontWeight: '600',
          textAlign: 'center',
          marginBottom: 12,
          color: IQES.muted,
          lineHeight: 23,
          letterSpacing: 0.2,
        }}
      >
        {subHeaderText}
      </Text>

      {contextTitle ? (
        <View
          style={{
            backgroundColor: IQES.purpleDeep,
            borderRadius: 999,
            paddingHorizontal: 12,
            paddingVertical: 6,
            marginBottom: 12,
            borderWidth: 1,
            borderColor: IQES.border,
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: '700',
              color: IQES.text,
            }}
          >
            {contextTitle}
          </Text>
        </View>
      ) : null}


      {/* Premium Verse Card - Always show verse with fallback */}
      {displayVerse && displayVerse.length > 0 && (
        <View style={{
          backgroundColor: IQES.panelDeep,
          borderRadius: 18,
          paddingVertical: 12,
          paddingHorizontal: 16,
          maxWidth: verseCardMaxWidth,
          marginTop: 0,
          alignSelf: 'center',
          borderWidth: 1,
          borderColor: IQES.border,
          // Ensure minimum width on mobile
          minWidth: verseCardMinWidth,
          ...Platform.select({
            ios: {
              shadowColor: IQES.gold,
              shadowOffset: { width: 0, height: 3 },
              shadowOpacity: 0.12,
              shadowRadius: 8,
            },
            android: {
              elevation: 2,
            },
            web: {
              boxShadow: '0 4px 12px rgba(248, 200, 78, 0.12)',
            },
          }),
        }}>
          <View style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'center'
          }}>
            <View style={{
              backgroundColor: "rgba(248, 200, 78, 0.13)",
              borderRadius: 10,
              padding: 7,
              marginRight: 10,
              marginTop: 2,
              minWidth: 32,
              minHeight: 32,
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: IQES.border,
            }}>
              <Ionicons
                name="book-outline"
                size={18}
                color={IQES.goldSoft}
                accessible={false}
              />
            </View>
            <Text
              style={{
                fontSize: Platform.select({
                  web: 15,
                  ios: 14,
                  android: 14,
                  default: 14
                }),
                color: IQES.text,
                fontStyle: 'italic',
                textAlign: 'left',
                lineHeight: Platform.select({
                  web: 22,
                  ios: 20,
                  android: 20,
                  default: 20
                }),
                fontWeight: '500',
                flex: 1,
                letterSpacing: 0.1,
                // Mobile-specific text rendering fixes
                includeFontPadding: Platform.OS === 'android' ? false : undefined,
                textAlignVertical: Platform.OS === 'android' ? 'center' : undefined,
              }}
              numberOfLines={undefined}
              ellipsizeMode="tail"
              accessible={true}
              accessibilityLabel={`Bible verse: ${displayVerse}`}
              // Force text to render on mobile
              allowFontScaling={true}
            >
              {displayVerse}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

export default ResultsHeaderBlock;
